/**
 * Type-safe localStorage utility with schema validation and error handling.
 * Provides compile-time type safety and runtime validation for stored data.
 */

/**
 * Storage key constants to prevent typos and improve maintainability
 */
export const STORAGE_KEYS = {
	SESSION_BANNER_DISMISSED: 'session-banner-dismissed',
	HEARTBEAT_SESSION_EXPIRY: 'heartbeat-last-session-expiry',
} as const;

/**
 * Data structure for session banner dismissal state
 */
export interface SessionBannerDismissedData {
	hideUntil?: number;
}

/**
 * Type for heartbeat session expiry timestamp
 */
export type HeartbeatSessionExpiry = number;

/**
 * Registry of all storage keys and their corresponding data types
 */
export interface StorageSchema {
	[STORAGE_KEYS.SESSION_BANNER_DISMISSED]: SessionBannerDismissedData;
	[STORAGE_KEYS.HEARTBEAT_SESSION_EXPIRY]: HeartbeatSessionExpiry;
}

/**
 * Union type of all valid storage keys
 */
export type StorageKey = keyof StorageSchema;

/**
 * Result type for storage operations that provides explicit error handling
 * instead of throwing exceptions
 */
export type StorageResult<T> =
	| { success: true; data: T }
	| {
			success: false;
			error: 'not_found' | 'parse_error' | 'validation_error' | 'storage_unavailable';
	  };

/**
 * Validation function type for storage values
 */
export type Validator<T> = (value: unknown) => value is T;

/**
 * Mapped type to ensure validators exist for all storage keys
 */
export type ValidatorMap = {
	[K in keyof StorageSchema]: Validator<StorageSchema[K]>;
};

/**
 * Predefined validators for storage data types
 */
export const validators: ValidatorMap = {
	[STORAGE_KEYS.SESSION_BANNER_DISMISSED]: (
		value: unknown,
	): value is SessionBannerDismissedData => {
		if (typeof value !== 'object' || value === null) {
			return false;
		}

		const obj = value as Record<string, unknown>;

		// If hideUntil exists, it must be a number
		if ('hideUntil' in obj) {
			return typeof obj.hideUntil === 'number';
		}

		return true;
	},

	[STORAGE_KEYS.HEARTBEAT_SESSION_EXPIRY]: (value: unknown): value is HeartbeatSessionExpiry => {
		return typeof value === 'number' && value > 0;
	},
};

/**
 * Type-safe localStorage getter with validation
 * @param key - The storage key to retrieve
 * @param validator - Function to validate the stored data
 * @returns Result object with success status and data or error
 */
export function getStorageValue<K extends StorageKey>(
	key: K,
	validator: Validator<StorageSchema[K]>,
	storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
): StorageResult<StorageSchema[K]> {
	try {
		const stored = storage.getItem(key);

		if (stored === null) {
			return { success: false, error: 'not_found' };
		}

		let parsed: unknown;
		try {
			parsed = JSON.parse(stored);
		} catch {
			// Clean up corrupted data
			storage.removeItem(key);
			return { success: false, error: 'parse_error' };
		}

		if (!validator(parsed)) {
			// Clean up invalid data
			storage.removeItem(key);
			return { success: false, error: 'validation_error' };
		}

		return { success: true, data: parsed };
	} catch {
		return { success: false, error: 'storage_unavailable' };
	}
}

/**
 * Type-safe localStorage setter
 * @param key - The storage key to set
 * @param value - The value to store
 * @param validator - Optional validator to check value before storing
 * @returns Result object with success status
 */
export function setStorageValue<K extends StorageKey>(
	key: K,
	value: StorageSchema[K],
	validator: Validator<StorageSchema[K]> | undefined,
	storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
): StorageResult<void> | Promise<StorageResult<void>> {
	try {
		// Optional validation before storing
		if (validator && !validator(value)) {
			return { success: false, error: 'validation_error' };
		}

		const setResult = storage.setItem(key, JSON.stringify(value)) as void | Promise<void>;

		// If setItem returns a Promise (axLocalStorage), wrap it in our result type
		if (setResult instanceof Promise) {
			return setResult
				.then(() => ({ success: true as const, data: undefined }))
				.catch(() => ({ success: false as const, error: 'storage_unavailable' as const }));
		}

		// Synchronous storage (regular localStorage)
		return { success: true, data: undefined };
	} catch {
		return { success: false, error: 'storage_unavailable' };
	}
}

/**
 * Remove a storage value
 * @param key - The storage key to remove
 * @returns Result object with success status
 */
export function removeStorageValue(
	key: StorageKey,
	storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
): StorageResult<void> {
	try {
		storage.removeItem(key);
		return { success: true, data: undefined };
	} catch {
		return { success: false, error: 'storage_unavailable' };
	}
}

/**
 * Factory function to create convenience API with custom storage
 * @param storage - Storage implementation to use
 * @returns Object with convenience functions for specific storage keys
 */
export const createStorageAPI = (storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>) => ({
	sessionBannerDismissed: {
		get: (): StorageResult<SessionBannerDismissedData> =>
			getStorageValue(
				STORAGE_KEYS.SESSION_BANNER_DISMISSED,
				validators[STORAGE_KEYS.SESSION_BANNER_DISMISSED],
				storage,
			),
		set: (value: SessionBannerDismissedData): StorageResult<void> | Promise<StorageResult<void>> =>
			setStorageValue(
				STORAGE_KEYS.SESSION_BANNER_DISMISSED,
				value,
				validators[STORAGE_KEYS.SESSION_BANNER_DISMISSED],
				storage,
			),
		remove: (): StorageResult<void> =>
			removeStorageValue(STORAGE_KEYS.SESSION_BANNER_DISMISSED, storage),
	},

	heartbeatSessionExpiry: {
		get: (): StorageResult<HeartbeatSessionExpiry> =>
			getStorageValue(
				STORAGE_KEYS.HEARTBEAT_SESSION_EXPIRY,
				validators[STORAGE_KEYS.HEARTBEAT_SESSION_EXPIRY],
				storage,
			),
		set: (value: HeartbeatSessionExpiry): StorageResult<void> | Promise<StorageResult<void>> =>
			setStorageValue(
				STORAGE_KEYS.HEARTBEAT_SESSION_EXPIRY,
				value,
				validators[STORAGE_KEYS.HEARTBEAT_SESSION_EXPIRY],
				storage,
			),
		remove: (): StorageResult<void> =>
			removeStorageValue(STORAGE_KEYS.HEARTBEAT_SESSION_EXPIRY, storage),
	},
});
